import { useEffect, useRef, useState } from 'react';
import Icon from '../common/Icon.jsx';

/**
 * ChatComposer — the bottom-anchored input field + send button.
 *
 * Behavioural notes:
 *   - Single-line textarea with auto-grow up to 4 rows. Beyond that, the
 *     internal scroll kicks in. We don't ever want the composer to fill
 *     the screen.
 *   - Submit on Enter; Shift+Enter inserts a newline.
 *   - Disabled when `isPending` is true so the user can't fire a second
 *     query while the first is in flight. The mock service already gates
 *     this client-side, but the real backend has hard rate limits.
 *   - When the screen receives a seeded prompt from the global context the
 *     parent passes it in via `seededValue`; we sync it into local state
 *     and clear after submission.
 */
export default function ChatComposer({ onSubmit, isPending, seededValue }) {
  const [value, setValue] = useState('');
  const [lastSeed, setLastSeed] = useState(seededValue);
  const textareaRef = useRef(null);

  // Adjusting state during render, not in an effect. React handles this by
  // re-running the component before it commits, so the textarea paints once
  // with the seeded text; the effect version painted the empty box first and
  // then replaced it. (react.dev — "You Might Not Need an Effect", adjusting
  // state when a prop changes.)
  if (seededValue && seededValue !== lastSeed) {
    setLastSeed(seededValue);
    setValue(seededValue);
  }

  // Auto-grow up to 4 lines (~96 px).
  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const next = Math.min(ta.scrollHeight, 96);
    ta.style.height = `${next}px`;
  }, [value]);

  const submit = () => {
    const trimmed = value.trim();
    if (!trimmed || isPending) return;
    onSubmit(trimmed);
    setValue('');
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const canSend = value.trim().length > 0 && !isPending;

  return (
    <div
      className="sticky bottom-0 -mx-5 border-t border-stroke/70 bg-bg-elevated/95 px-5 pb-3 pt-3 backdrop-blur-md"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 12px)' }}
    >
      <div className="flex items-end gap-2">
        <div className="flex flex-1 items-end rounded-2xl bg-bg-card px-3 py-2 ring-1 ring-stroke focus-within:ring-f1-red/60">
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={isPending ? 'Waiting on Oracle…' : 'Ask the Oracle anything…'}
            disabled={isPending}
            className="max-h-24 min-h-[24px] w-full resize-none bg-transparent text-sm text-ink-primary placeholder:text-ink-tertiary focus:outline-none disabled:opacity-50"
            aria-label="Message the Oracle"
          />
        </div>
        <button
          onClick={submit}
          disabled={!canSend}
          aria-label="Send message"
          className={`pressable flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl transition-all ${
            canSend
              ? 'bg-f1-red text-white shadow-glow-red-soft hover:bg-f1-redBright'
              : 'bg-bg-card2 text-ink-tertiary ring-1 ring-stroke'
          }`}
        >
          <Icon name="send" size={18} />
        </button>
      </div>

      <p className="mt-2 text-center text-[10px] text-ink-muted">
        Oracle can be wrong about real-time data. Always verify against official sources.
      </p>
    </div>
  );
}
